import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef } from 'react';
import { ResourceCalendar } from '../../../../../types';
import ResourceCalendarsAutoComplete from '../../../../shared/resource-calendars-autocomplete';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import { SectionHeader } from '../../../../shared/section-header';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import BulkOpTag from './bulk-op-tag';
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Star from '@mui/icons-material/Star';

// Design: input width for the new UI.
const newUiFieldWidth = 620;

interface StepSelectResourceProps {
  control: Control<BulkOpsFormValues>;
  setValue: UseFormSetValue<BulkOpsFormValues>;
  watch: UseFormWatch<BulkOpsFormValues>;
  calendars: ResourceCalendar[];
  errors: FieldErrors<BulkOpsFormValues>;
  isNewUi?: boolean;
}

export default function StepConfiguration({
  control,
  setValue,
  watch,
  calendars,
  errors,
  isNewUi = false,
}: StepSelectResourceProps) {
  const theme = useTheme();
  const autocompleteKey = useRef(new Date().getTime().toString());
  const resourceIds = watch('resourceIds') || [];
  const startTime = dayjs(watch('startDate')!);
  const endTime = dayjs(watch('endDate')!);
  const selectedResources =
    resourceIds && resourceIds.length > 0
      ? (resourceIds
          .map((r) => calendars.find((c) => c.resourceId === r))
          .filter(Boolean) as ResourceCalendar[])
      : [];

  const removeResource = (resourceIdToRemove: string) => {
    setValue(
      'resourceIds',
      resourceIds.filter((resourceId) => resourceId !== resourceIdToRemove)
    );
  };

  const resourceAutocomplete = (
    <Controller
      name={'resourceIds'}
      control={control}
      render={() => (
        <Box
          sx={{
            width: isNewUi ? newUiFieldWidth : 400,
            // Design: the placeholder reads as regular input text.
            ...(isNewUi && {
              '& input::placeholder': {
                opacity: 1,
                color: theme.palette.semantic.text.body,
              },
            }),
          }}
        >
          <ResourceCalendarsAutoComplete
            newKey={`${autocompleteKey.current}`}
            size={isNewUi ? 'small' : 'medium'}
            resourceCalendars={calendars}
            setAutoCompleteValue={(value) => {
              // The autocomplete emits the calendar's email (or its id when it
              // has none); bulk operations store resource ids.
              const resourceId = calendars.find(
                (c) => c.resourceEmail === value || c.resourceId === value
              )?.resourceId;
              if (!resourceId) return;

              if (resourceIds && !resourceIds.includes(resourceId)) {
                setValue('resourceIds', [...resourceIds!, resourceId]);
              }
              autocompleteKey.current = new Date().getTime().toString();
            }}
            hasError={!!errors.resourceIds}
            helperText={
              errors.resourceIds
                ? errors.resourceIds?.message
                : isNewUi || (resourceIds && resourceIds.length > 0)
                ? ''
                : ' '
            }
            placeholder={isNewUi ? 'Search for a room calendar' : undefined}
          />
        </Box>
      )}
    />
  );

  const dateRangePicker = (
    <DateRangePicker
      disableHighlightToday
      sx={{ width: isNewUi ? newUiFieldWidth : 250 }}
      value={[startTime, endTime]}
      onChange={(newValue) => {
        if (newValue && newValue[0]) {
          setValue('startDate', newValue[0].toDate().getTime(), {
            shouldDirty: true,
          });
        }
        if (newValue && newValue[1]) {
          setValue('endDate', newValue[1]!.toDate().getTime(), {
            shouldDirty: true,
          });
        }
      }}
      label={'Date Range'}
      slots={{ field: SingleInputDateRangeField }}
      slotProps={{
        textField: {
          size: isNewUi ? 'small' : undefined,
          error: !!errors.startDate || !!errors.endDate,
          helperText:
            errors && errors.startDate
              ? errors.startDate.message
              : errors.endDate
              ? errors.endDate.message
              : isNewUi
              ? ''
              : ' ',
        },
      }}
    />
  );

  if (isNewUi) {
    return (
      <Stack gap={3} width={'100%'}>
        <FormSectionHeader
          descriptionVariant="body2"
          description="Choose the rooms and date range for this bulk operation. The selected rooms will be removed from all events in that range."
        >
          Select Configuration
        </FormSectionHeader>
        <Stack gap={2}>
          <FormSectionHeader
            descriptionVariant="body2"
            description="Provide one or more resource calendars for this bulk operation."
          >
            Resource
          </FormSectionHeader>
          <Stack gap={1}>
            {resourceAutocomplete}
            {selectedResources.length > 0 && (
              <Stack
                flexDirection="row"
                gap={1.25}
                flexWrap="wrap"
                sx={{ py: 1 }}
              >
                {selectedResources.map((resource) => (
                  <BulkOpTag
                    key={resource.resourceId}
                    label={resource.resourceName}
                    icon={Star}
                    onDelete={() => removeResource(resource.resourceId)}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack gap={2}>
          <FormSectionHeader
            descriptionVariant="body2"
            description="Provide a valid date range to remove rooms from the events that reside in this date range."
          >
            Date range
          </FormSectionHeader>
          {dateRangePicker}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={1} width={'100%'}>
      <SectionHeader description="Provide one or more resource calendars for this bulk operation.">
        Select Resource
      </SectionHeader>
      <Stack>
        <Stack gap={1}>
          <Stack flexDirection="row" gap={1}>
            {resourceAutocomplete}
          </Stack>
          <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {selectedResources && selectedResources.length > 0
              ? selectedResources.map((resource) => {
                  return (
                    <Chip
                      key={resource.resourceId}
                      label={resource.resourceName}
                      size="small"
                      avatar={<Avatar children={<Star />} />}
                      onDelete={() => removeResource(resource.resourceId)}
                    />
                  );
                })
              : null}
          </Stack>
        </Stack>
      </Stack>
      <SectionHeader
        description="Provide a valid date range to remove rooms from the events that reside
        in this date range."
      >
        Date Range
      </SectionHeader>
      <Stack>{dateRangePicker}</Stack>
    </Stack>
  );
}
